import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, X } from 'lucide-react'
import type { components } from '@/api/schema'
import { useAuthenticatedClient } from '@/api/queries'
import { cn, formatEventTime, formatNumber, parseUTCDate } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

type FeedHealth = components['schemas']['CatalogFeedHealthRead']
type FeedRow = components['schemas']['CatalogFeedRowRead']
type FeedDay = components['schemas']['CatalogFeedDayRead']
type Attention = components['schemas']['CatalogFeedAttentionRead']
type Run = components['schemas']['CatalogSourceRunRead']

// A run still "running" this long after it started was interrupted (the process was stopped).
const INTERRUPTED_AFTER_MS = 12 * 60 * 60 * 1000
const ATTENTION_PREVIEW = 12

const ISSUE_ORDER: Attention['issue'][] = [
  'failed',
  'empty',
  'not_checked',
  'fewer_products',
  'never_checked',
]

const ISSUE_LABELS: Record<Attention['issue'], string> = {
  failed: 'Failed',
  empty: 'Empty feed',
  not_checked: 'Overdue',
  fewer_products: 'Fewer products',
  never_checked: 'Never checked',
}

type StatusFilter = 'all' | 'failed' | 'empty' | 'running' | 'succeeded'

function formatAgo(value: string | null | undefined): string {
  if (!value) return 'Never'
  const minutes = Math.max(0, Math.round((Date.now() - parseUTCDate(value).getTime()) / 60000))
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours} h ago`
  return `${Math.round(hours / 24)} days ago`
}

function formatDuration(run: Run): string {
  if (!run.finished_at) return '—'
  const seconds = Math.round(
    (parseUTCDate(run.finished_at).getTime() - parseUTCDate(run.started_at).getTime()) / 1000,
  )
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  return minutes < 60
    ? `${minutes}m ${seconds % 60}s`
    : `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

function formatDay(day: string, style: 'short' | 'weekday' = 'short'): string {
  const [y, m, d] = day.split('-').map(Number)
  const date = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1))
  return style === 'weekday'
    ? date.toLocaleDateString(undefined, { weekday: 'narrow', timeZone: 'UTC' })
    : date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      })
}

function runStatus(run: Run): { label: string; tone: Tone } {
  if (run.status === 'running') {
    const interrupted = Date.now() - parseUTCDate(run.started_at).getTime() > INTERRUPTED_AFTER_MS
    return interrupted
      ? { label: "Didn't finish", tone: 'bad' }
      : { label: 'Running', tone: 'active' }
  }
  if (run.status === 'succeeded') return { label: 'Checked', tone: 'good' }
  if (run.status === 'failed') return { label: 'Failed', tone: 'bad' }
  if (run.status === 'empty') return { label: 'Empty', tone: 'warn' }
  if (run.status === 'partial') return { label: 'Partial', tone: 'warn' }
  return { label: 'Skipped', tone: 'none' }
}

type Tone = 'good' | 'warn' | 'bad' | 'active' | 'none'

const TONE_CELL: Record<Tone, string> = {
  good: 'bg-green-600 border-green-600',
  warn: 'bg-amber-400 border-amber-400',
  bad: 'bg-red-600 border-red-600',
  active: 'bg-background border-foreground/40 border-dashed',
  none: 'bg-background border-border',
}

const TONE_TEXT: Record<Tone, string> = {
  good: 'text-green-800 bg-green-100',
  warn: 'text-amber-900 bg-amber-100',
  bad: 'text-red-800 bg-red-100',
  active: 'text-foreground bg-muted',
  none: 'text-muted-foreground bg-muted',
}

function dayTone(day: FeedDay): Tone {
  if (day.failed) return 'bad'
  if (day.empty || day.partial) return 'warn'
  if (day.succeeded) return 'good'
  if (day.running) return 'active'
  return 'none'
}

function daySummary(row: FeedRow, day: FeedDay, tracked: number): string {
  const parts: string[] = []
  if (row.scope_type === 'manufacturer') {
    parts.push(
      `${formatNumber(day.manufacturers_checked, 0)} of ${formatNumber(tracked, 0)} checked`,
    )
  } else if (day.succeeded) {
    parts.push(`${day.succeeded} checked`)
  }
  if (day.failed) parts.push(`${day.failed} failed`)
  if (day.empty) parts.push(`${day.empty} empty`)
  if (day.partial) parts.push(`${day.partial} partial`)
  if (day.running) parts.push(`${day.running} running`)
  return `${formatDay(day.day)}: ${parts.length ? parts.join(', ') : 'no runs'}`
}

function useFeedHealth() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: ['catalog-sources', 'health'],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/catalog-sources/health', {
        params: { query: { days: 14 } },
      })
      if (error) throw error
      return data!
    },
    refetchInterval: 60_000,
  })
}

interface RunFilters {
  row?: FeedRow
  day?: string
  status: StatusFilter
  page: number
}

function useRuns(filters: RunFilters) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: [
      'catalog-sources',
      'runs',
      filters.row?.key,
      filters.day,
      filters.status,
      filters.page,
    ],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/catalog-sources/runs', {
        params: {
          query: {
            row: filters.row?.key,
            day: filters.day,
            status: filters.status === 'all' ? undefined : filters.status,
            page: filters.page,
            limit: 25,
          },
        },
      })
      if (error) throw error
      return data!
    },
  })
}

export function CatalogSourcesPage() {
  const health = useFeedHealth()
  const [filters, setFilters] = useState<RunFilters>({
    status: 'all',
    page: 1,
  })
  const runs = useRuns(filters)

  return (
    <div className="mx-auto max-w-7xl space-y-8">
      <h1 className="text-2xl font-bold tracking-tight">Catalog sources</h1>

      {health.isLoading ? (
        <HealthSkeleton />
      ) : health.error || !health.data ? (
        <p className="rounded-lg border px-4 py-6 text-sm text-muted-foreground">
          Feed health couldn’t load. Refresh the page to try again.
        </p>
      ) : (
        <>
          <AttentionSection data={health.data} />
          <SourcesGrid
            data={health.data}
            selected={filters}
            onSelect={(row, day) =>
              setFilters((current) =>
                current.row?.key === row.key && current.day === day
                  ? { ...current, row: undefined, day: undefined, page: 1 }
                  : { ...current, row, day, page: 1 },
              )
            }
          />
        </>
      )}

      <RunsSection
        filters={filters}
        setFilters={setFilters}
        data={runs.data}
        isLoading={runs.isLoading}
        failed={Boolean(runs.error)}
      />
    </div>
  )
}

function AttentionSection({ data }: { data: FeedHealth }) {
  const groups = useMemo(() => {
    const byIssue = new Map<Attention['issue'], Attention[]>()
    for (const item of data.attention) {
      byIssue.set(item.issue, [...(byIssue.get(item.issue) ?? []), item])
    }
    return ISSUE_ORDER.filter((issue) => byIssue.has(issue)).map((issue) => ({
      issue,
      items: byIssue.get(issue) ?? [],
    }))
  }, [data.attention])

  return (
    <section aria-labelledby="attention-heading" className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 id="attention-heading" className="text-base font-semibold">
          Needs attention
        </h2>
        <p className="text-sm tabular-nums text-muted-foreground">
          {formatNumber(data.checked_today, 0)} of {formatNumber(data.tracked_manufacturers, 0)}{' '}
          manufacturers checked today
        </p>
      </div>
      {groups.length === 0 ? (
        <p className="flex items-center gap-2 rounded-lg border px-4 py-3 text-sm">
          <CheckCircle2 className="h-4 w-4 text-green-600" aria-hidden />
          Every tracked manufacturer was checked on time.
        </p>
      ) : (
        <div className="divide-y rounded-lg border">
          {groups.map((group) => (
            <AttentionGroup key={group.issue} issue={group.issue} items={group.items} />
          ))}
        </div>
      )}
    </section>
  )
}

function attentionDetail(item: Attention): string {
  switch (item.issue) {
    case 'failed':
    case 'empty':
      return item.streak_days > 1 ? `${item.streak_days} nights in a row` : ''
    case 'not_checked':
      return `Last checked ${formatAgo(item.last_success_at)}`
    case 'fewer_products':
      return `${formatNumber(item.observed_count ?? 0, 0)}, was ${formatNumber(item.previous_count ?? 0, 0)}`
    default:
      return ''
  }
}

function AttentionGroup({ issue, items }: { issue: Attention['issue']; items: Attention[] }) {
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? items : items.slice(0, ATTENTION_PREVIEW)
  const tone: Tone = issue === 'failed' ? 'bad' : issue === 'never_checked' ? 'none' : 'warn'
  return (
    <div className="grid gap-3 px-4 py-3 md:grid-cols-[180px_1fr]">
      <div className="flex items-center gap-2 md:items-start">
        <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', TONE_TEXT[tone])}>
          {ISSUE_LABELS[issue]}
        </span>
        <span className="text-sm tabular-nums text-muted-foreground">{items.length}</span>
      </div>
      <ul className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
        {visible.map((item) => {
          const detail = attentionDetail(item)
          return (
            <li key={item.manufacturer.id} className="min-w-0">
              <Link
                to="/manufacturers/$manuId"
                params={{ manuId: item.manufacturer.uuid }}
                className="font-medium text-primary hover:underline"
              >
                {item.manufacturer.name}
              </Link>
              {detail && <span className="ml-1.5 text-muted-foreground">{detail}</span>}
            </li>
          )
        })}
        {items.length > ATTENTION_PREVIEW && (
          <li>
            <button
              type="button"
              className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded ? 'Show fewer' : `Show all ${formatNumber(items.length, 0)}`}
            </button>
          </li>
        )}
      </ul>
    </div>
  )
}

function SourcesGrid({
  data,
  selected,
  onSelect,
}: {
  data: FeedHealth
  selected: RunFilters
  onSelect: (row: FeedRow, day: string) => void
}) {
  const days = data.rows[0]?.days ?? []
  return (
    <section aria-labelledby="sources-heading" className="space-y-3">
      <h2 id="sources-heading" className="text-base font-semibold">
        Sources
      </h2>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b text-xs text-muted-foreground">
              <th scope="col" className="px-4 py-2 text-left font-medium">
                Source
              </th>
              <th scope="col" className="hidden px-2 py-2 text-left font-medium md:table-cell">
                Last checked
              </th>
              {days.map((day, index) => (
                <th
                  key={day.day}
                  scope="col"
                  className={cn(
                    'px-0.5 py-2 text-center font-medium tabular-nums',
                    index < days.length - 7 && 'hidden lg:table-cell',
                  )}
                >
                  <span className="sr-only">{formatDay(day.day)}</span>
                  <span aria-hidden>{formatDay(day.day, 'weekday')}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.key} className="border-b last:border-b-0">
                <th scope="row" className="px-4 py-2.5 text-left font-medium">
                  {row.label}
                  <LastChecked row={row} className="block text-xs font-normal md:hidden" />
                </th>
                <td className="hidden whitespace-nowrap px-2 py-2.5 md:table-cell">
                  <LastChecked row={row} />
                </td>
                {row.days.map((day, index) => {
                  const tone = dayTone(day)
                  const label = daySummary(row, day, data.tracked_manufacturers)
                  const isSelected = selected.row?.key === row.key && selected.day === day.day
                  return (
                    <td
                      key={day.day}
                      className={cn(
                        'px-0.5 py-2.5 text-center',
                        index < days.length - 7 && 'hidden lg:table-cell',
                      )}
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            aria-label={label}
                            aria-pressed={isSelected}
                            disabled={tone === 'none'}
                            onClick={() => onSelect(row, day.day)}
                            className={cn(
                              'inline-block h-5 w-5 rounded-[4px] border align-middle transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-default',
                              TONE_CELL[tone],
                              isSelected && 'ring-2 ring-foreground ring-offset-2',
                            )}
                          />
                        </TooltipTrigger>
                        <TooltipContent>{label}</TooltipContent>
                      </Tooltip>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function LastChecked({ row, className }: { row: FeedRow; className?: string }) {
  return row.stale ? (
    <span className={cn('inline-flex items-center gap-1 text-red-700', className)}>
      <AlertTriangle className="inline h-3.5 w-3.5" aria-hidden />
      {row.last_success_at ? formatAgo(row.last_success_at) : 'Never'}
    </span>
  ) : (
    <span className={cn('text-muted-foreground', className)}>{formatAgo(row.last_success_at)}</span>
  )
}

const NETWORK_ERRORS = new Set([
  'ConnectError',
  'ConnectTimeout',
  'RemoteProtocolError',
  'OSError',
  'gaierror',
])
const TIMEOUT_ERRORS = new Set(['TimeoutError', 'ReadTimeout', 'WriteTimeout', 'PoolTimeout'])

// Plain words for why a run failed; the stored error stays in the tooltip for engineers.
function runProblem(run: Run): string {
  const source = run.source.key === 'aq_feed' ? 'AutoQuotes' : run.source.name
  const errorClass = run.error_class ?? ''
  if (errorClass === 'AQFeedEmptyResponseError') return 'AutoQuotes sent no product list'
  if (TIMEOUT_ERRORS.has(errorClass)) return `${source} didn’t respond in time`
  if (
    NETWORK_ERRORS.has(errorClass) ||
    /network is unreachable|connection (refused|reset)/i.test(run.error_message ?? '')
  ) {
    return `Couldn’t reach ${source}`
  }
  if (errorClass === 'AutoQuotesCategoryIdentityError')
    return 'AutoQuotes categories changed; needs a developer'
  return 'Check failed; needs a developer'
}

function RunScope({ run }: { run: Run }) {
  if (run.manufacturer) {
    return (
      <Link
        to="/manufacturers/$manuId"
        params={{ manuId: run.manufacturer.uuid }}
        className="font-medium text-primary hover:underline"
      >
        {run.manufacturer.name}
      </Link>
    )
  }
  if (run.scope_key === 'manufacturers') return <span>Manufacturer list</span>
  if (run.scope_key === 'categories') return <span>Categories</span>
  if (run.scope_key === 'shipping_estimate_generation') return <span>Estimate build</span>
  if (run.scope_key === 'shipment_facts') return <span>Shipment facts</span>
  return <span className="break-all text-muted-foreground">{run.scope_key || '—'}</span>
}

function RunCounts({ run }: { run: Run }) {
  if (run.observed_count == null) return <span className="text-muted-foreground">—</span>
  return (
    <span className="tabular-nums">
      {formatNumber(run.observed_count, 0)}
      {run.expected_count != null && run.expected_count !== run.observed_count && (
        <span className="text-muted-foreground"> of {formatNumber(run.expected_count, 0)}</span>
      )}
    </span>
  )
}

function StatusPill({ run }: { run: Run }) {
  const status = runStatus(run)
  return (
    <span
      className={cn(
        'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
        TONE_TEXT[status.tone],
      )}
    >
      {status.label}
    </span>
  )
}

function RunsSection({
  filters,
  setFilters,
  data,
  isLoading,
  failed,
}: {
  filters: RunFilters
  setFilters: React.Dispatch<React.SetStateAction<RunFilters>>
  data: components['schemas']['PaginatedResponse_CatalogSourceRunRead_'] | undefined
  isLoading: boolean
  failed: boolean
}) {
  return (
    <section aria-labelledby="runs-heading" className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h2 id="runs-heading" className="mr-auto text-base font-semibold">
          Runs
        </h2>
        {filters.row && filters.day && (
          <Badge variant="outline" className="h-8 gap-1.5 rounded-md px-2.5 text-sm font-normal">
            {filters.row.label} · {formatDay(filters.day)}
            <button
              type="button"
              aria-label="Clear source and day"
              className="rounded text-muted-foreground hover:text-foreground"
              onClick={() =>
                setFilters((current) => ({
                  ...current,
                  row: undefined,
                  day: undefined,
                  page: 1,
                }))
              }
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </Badge>
        )}
        <Select
          value={filters.status}
          onValueChange={(value) =>
            setFilters((current) => ({
              ...current,
              status: value as RunFilters['status'],
              page: 1,
            }))
          }
        >
          <SelectTrigger className="w-[150px]" aria-label="Status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="empty">Empty</SelectItem>
            <SelectItem value="running">Running</SelectItem>
            <SelectItem value="succeeded">Checked</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </div>
      ) : failed ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          Runs couldn’t load. Refresh the page to try again.
        </p>
      ) : !data?.items.length ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No runs match these filters
        </p>
      ) : (
        <>
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead>Checked</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead className="text-right">Took</TableHead>
                  <TableHead className="text-right">Products</TableHead>
                  <TableHead className="text-right">Changed</TableHead>
                  <TableHead className="text-right">Missing</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((run) => (
                  <TableRow key={run.id}>
                    <TableCell className="align-top text-muted-foreground">
                      {run.source.name}
                    </TableCell>
                    <TableCell className="max-w-[260px] whitespace-normal align-top">
                      <RunScope run={run} />
                      {run.error_class && (
                        <p
                          className="mt-0.5 text-xs text-red-700"
                          title={run.error_message ?? undefined}
                        >
                          {runProblem(run)}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      <StatusPill run={run} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap align-top text-muted-foreground">
                      {formatEventTime(run.started_at)}
                    </TableCell>
                    <TableCell className="text-right align-top tabular-nums text-muted-foreground">
                      {formatDuration(run)}
                    </TableCell>
                    <TableCell className="text-right align-top">
                      <RunCounts run={run} />
                    </TableCell>
                    <TableCell className="text-right align-top tabular-nums">
                      {run.changed_count == null ? '—' : formatNumber(run.changed_count, 0)}
                    </TableCell>
                    <TableCell className="text-right align-top tabular-nums">
                      {run.absent_count == null ? '—' : formatNumber(run.absent_count, 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <ul className="space-y-2 md:hidden">
            {data.items.map((run) => (
              <li key={run.id} className="rounded-lg border p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <RunScope run={run} />
                    <p className="text-xs text-muted-foreground">
                      {run.source.name} · {formatEventTime(run.started_at)}
                    </p>
                  </div>
                  <StatusPill run={run} />
                </div>
                {run.error_class && <p className="mt-2 text-xs text-red-700">{runProblem(run)}</p>}
                <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <dt className="text-muted-foreground">Products</dt>
                    <dd>
                      <RunCounts run={run} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Changed</dt>
                    <dd className="tabular-nums">
                      {run.changed_count == null ? '—' : formatNumber(run.changed_count, 0)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Missing</dt>
                    <dd className="tabular-nums">
                      {run.absent_count == null ? '—' : formatNumber(run.absent_count, 0)}
                    </dd>
                  </div>
                </dl>
              </li>
            ))}
          </ul>

          <div className="flex items-center justify-between py-1">
            <p className="text-sm text-muted-foreground">
              Page {data.page} of {Math.max(data.pages, 1)} ({formatNumber(data.total, 0)} runs)
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setFilters((current) => ({
                    ...current,
                    page: Math.max(1, current.page - 1),
                  }))
                }
                disabled={filters.page <= 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setFilters((current) => ({
                    ...current,
                    page: current.page + 1,
                  }))
                }
                disabled={filters.page >= data.pages}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </section>
  )
}

function HealthSkeleton() {
  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-24 w-full" />
      </div>
      <div className="space-y-3">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  )
}
